import { ref, computed } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { useAppLoading } from '@/stores/app/loading';
import { useAppPagination } from '@/stores/app/pagination';
import { useAppRoute } from '@/stores/app/route';
import { useAppSnackbar } from '@/stores/app/snackbar';

import { deleteLibraryDocument, initLibrary, libraryGetDocuments } from '@/api/library';
import {
  recruitJsonDuplicate,
  recruitJsonMakePrivateMe,
  recruitJsonMakePrivateUs,
  recruitJsonMakeHidden,
} from '@/api/recruitJson';

import { isEqual } from 'lodash-es';
import { removeEmptyParams } from '@/stores/filters/helpers';
import { useSubMenu } from '@/stores/filters/subMenu';
import { useFilters } from '@/stores/filters/filters';
import { getOtherLibraryTypes } from '@/stores/library/helpers';

export const useLibrary = defineStore('library', () => {
  // other stores
  const { setLoading } = useAppLoading();
  const { updatePagination } = useAppPagination();
  const routeStore = useAppRoute();
  const { updateFilters, sendFilterRequest } = useFilters();
  const { myJobsEnabled, searchTerm, activeFilters } = storeToRefs(useFilters());
  const { addErrorSnackbar, addSuccessSnackbar } = useAppSnackbar();

  // state
  const documents = ref([]);
  const totals = ref({});
  const menuPages = ref([]);
  const defaultPage = ref('');
  const applicationReportingEnabled = ref(false);
  const otherDocumentTypeTotals = ref([]);
  const otherDocumentTypesLoading = ref(false);

  // getters
  const libraryParams = computed(() => {
    const { selectedSubMenuOption } = storeToRefs(useSubMenu());

    if (!selectedSubMenuOption.value) return false;

    return {
      sort: selectedSubMenuOption.value || '',
    };
  });

  // mutations
  const removeDocument = (documentId) => {
    documents.value = documents.value.filter((document) => document.id !== documentId);
  };

  const updateOtherDocumentTypeTotals = (newTotals) => {
    otherDocumentTypeTotals.value = newTotals;
  };

  const updateOtherDocumentTypesLoading = (newLoading) => {
    otherDocumentTypesLoading.value = newLoading;
  };

  // actions
  const loadBreadCrumbs = async () => {
    try {
      const config = await initLibrary();
      menuPages.value = config.menuPages;
      defaultPage.value = config.defaultPage;
      applicationReportingEnabled.value = config.applicationReportingEnabled;
    } catch (error) {
      addErrorSnackbar({ text: `Initialize library breadcrumbs - ${error.message}` });
      throw error;
    }
  };

  const init = async ({ router }) => {
    setLoading({ pageLoading: true });

    try {
      /* load sidebar first to get user's default library page */
      await loadBreadCrumbs();

      /* if user navigates to library root, forward them to their 'default' library page */
      if (routeStore.route.name === 'document-library') {
        router.replace({ name: defaultPage.value });
      } else if (routeStore.route.name === 'requisition-library') {
        router.replace({ name: 'open-requisitions' });
      }
    } catch (error) {
      addErrorSnackbar({ text: `Initialize library - ${error.message}` });
      throw error;
    }
  };

  const loadDocuments = async ({
    mode,
    routeQuery,
    router,
    selectedFilters,
    page,
    isBrowserNavigated = false,
    searchTerm: newSearchTerm,
    selectedMyJobs,
  }) => {
    setLoading({ listLoading: true, filtersLoading: true });
    otherDocumentTypeTotals.value = [];

    try {
      let params = routeQuery || {};

      if (libraryParams.value && !isBrowserNavigated) {
        params = { ...params, ...libraryParams.value };
      }

      params.page = page || undefined;

      const parameters = {
        mode,
        params,
        selectedMyJobs,
        searchTerm: newSearchTerm,
        selectedFilters,
      };

      const libraryDocuments = await libraryGetDocuments(parameters);

      /* once filters are received, dispatch update to filters and submenu */
      updateFilters({
        filters: libraryDocuments.filters,
        subMenuOptions: libraryDocuments.sorting,
        myJobsEnabled: libraryDocuments.owned,
        searchTerm: libraryDocuments.textFilter,
      });

      documents.value = libraryDocuments.documents;
      totals.value = libraryDocuments.totals;
      updatePagination(libraryDocuments.pagination);

      if (
        router
        && !isEqual(
          removeEmptyParams(routeStore.route?.query),
          removeEmptyParams(routeQuery),
        )
      ) {
        const newQuery = { ...selectedFilters, ...params };

        if (myJobsEnabled.value) {
          newQuery.owned = myJobsEnabled.value;
        }

        if (searchTerm.value) {
          newQuery.q = searchTerm.value;
        }

        router.push({
          query: newQuery,
        }).catch(() => true); // ignore duplicate navigation error due to global filter selection
      }

      /* If the mode is 'flagship' and there are no active filters,
      we don't need to load other document types */
      if (!libraryDocuments.documents?.length
          && !(mode === 'flagship' && !activeFilters.value)) {
        getOtherLibraryTypes({
          ...parameters,
          updateTotals: updateOtherDocumentTypeTotals,
          updateLoading: updateOtherDocumentTypesLoading,
          menuPages: menuPages.value,
        });
      }
    } catch (error) {
      addErrorSnackbar({ text: `Load library documents - ${error.message}` });
      throw error;
    } finally {
      setLoading({
        filtersLoading: false, listLoading: false, sidebarLoading: false, pageLoading: false,
      });
    }
  };

  const deleteDocument = async ({ id, documentType }) => {
    const documentName = documentType === 'email' ? 'Email' : 'Job';

    try {
      setLoading({ pageLoading: true });

      const { success } = await deleteLibraryDocument(id);
      if (success) {
        removeDocument(id);
        addSuccessSnackbar({ text: `${documentName} deleted` });
      }
    } catch (error) {
      addErrorSnackbar({
        text: `Unable to delete ${documentName} - ${error.message}`,
      });
      throw error;
    } finally {
      setLoading({ pageLoading: false });
    }
  };

  const duplicateDocument = async ({
    jobId, isPrivate, title, from,
  }) => {
    try {
      setLoading({ pageLoading: true });

      const { id } = await recruitJsonDuplicate({ jobId, title, from });
      if (!id) {
        return null;
      }

      if (isPrivate === 1) {
        await recruitJsonMakePrivateMe({ jobId: id });
      }
      if (isPrivate === 2) {
        await recruitJsonMakePrivateUs({ jobId: id });
      }

      addSuccessSnackbar({ text: 'Duplicate created' });
      return id;
    } catch (error) {
      addErrorSnackbar({ text: `Duplicate error - ${error.message}` });
      throw error;
    } finally {
      setLoading({ pageLoading: false });
    }
  };

  const toggleFlagshipHidden = async ({ id, isHidden, router }) => {
    try {
      setLoading({ pageLoading: true });

      const { success } = await recruitJsonMakeHidden({ jobId: id, isHidden });
      if (success) {
        const text = isHidden
          ? 'Your team can no longer see this job in the Flagship library'
          : 'Your team can now see this job in the Flagship library';

        addSuccessSnackbar({ text });

        // Trigger another filter request with the current filters to update documents
        sendFilterRequest({ router });
      }
    } catch (error) {
      addErrorSnackbar({ text: `Error toggling flagship job ${id} - ${error.message}` });
      throw error;
    } finally {
      setLoading({ pageLoading: false });
    }
  };

  const duplicateDocumentAndRedirect = async ({
    jobId, isPrivate, title, from, router,
  }) => {
    const newJobId = await duplicateDocument({
      jobId, isPrivate, title, from, router,
    });

    if (newJobId) {
      await router.push({
        name: 'editor-view-reload',
        params: { id: newJobId },
        query: { ...routeStore.route?.query, duplicate: 'true' },
      });
    }
  };

  return {
    // state
    documents,
    totals,
    applicationReportingEnabled,
    otherDocumentTypesLoading,
    otherDocumentTypeTotals,
    menuPages,
    defaultPage,

    // getters
    libraryParams,

    // actions
    init,
    loadDocuments,
    deleteDocument,
    duplicateDocument,
    duplicateDocumentAndRedirect,
    toggleFlagshipHidden,
  };
});
